<template>
  <div :class="errorMessages ? 'v-input--error' : undefined">
    <multiselect
      v-bind="attrsOnDefault"
      :options="filteredOptions"
      :internal-search="filter === undefined"
      @search-change="searchChange"
      :openDirection="openDirection === 'auto' ? '' : openDirection"
    >
      <template v-slot:noResult>データがありません</template>
      <template v-slot:noOptions>データがありません</template>
      <template v-slot:maxElements>
        <small>最大{{ $attrs.max }}個まで選択可能です。</small>
      </template>
      <template
        v-for="(_, name) in $slots"
        v-slot:[`${name}`]="props"
        :key="name"
      >
        <slot :name="name" v-bind="props"></slot>
      </template>
    </multiselect>
    <div class="v-input__details" v-if="!hideDetails">
      <v-messages
        :active="showErrorMessage"
        :messages="errorMessages"
      ></v-messages>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import Multiselect from "vue-multiselect";

/**
 * Vue-multiselectのラッパーコンポーネント
 * VuetifyやVeeValidateの兼ね合いで色々調整しないといけないので
 * 注意：
 * 同じDropDown系のAppSelectとAppSelectSearchも複数選択が可能なので、
 * そちらがお薦めです。なんか既存の仕様が満足できない場合、AppMultiSelectを使う風で良い
 */
export default defineComponent({
  name: "AppMultiSelect",
  inheritAttrs: false,
  components: { Multiselect },
  props: {
    /** 選択肢 */
    options: {
      type: Array as PropType<unknown[]>,
      required: true,
    },
    /** プルダウンの展開方向(デフォルトはbottom)
     * "auto": 自動
     * top：上へ展開
     * bottom: 下へ展開
     * */
    openDirection: {
      type: String as PropType<"auto" | "top" | "bottom">,
      required: false,
      default: "bottom",
    },
    /**
     * searchの際にdefaultだとlabel部分しか検索対象にならないので
     * 自由にカスタマイズ出来るためのfunction
     * @see https://github.com/shentao/vue-multiselect/issues/244
     * 文字列で検索対象のパスを渡せば使う側は楽だけどコンパイラーが検知出来なくなるので悩みどころ
     */
    filter: {
      type: [Function] as PropType<
        undefined | ((query: string, options: unknown[]) => unknown[])
      >,
      required: false,
      default: undefined,
    },
    /**
     * 配列のソートが必要な場合のみ設定する
     */
    sortOrders: {
      type: Array as PropType<
        | [string | ((e: any) => unknown), "asc" | "desc"][]
        | [
            string | ((e: any) => unknown),
            "asc" | "desc",
            { nullLast: boolean },
          ][]
        | undefined
      >,
      required: false,
      default: undefined,
    },
    /**
     * VeeValidateのエラーメッセージ
     */
    errorMessages: {
      type: String as PropType<string | undefined>,
      required: false,
    },
    hideDetails: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      /** 検索文字列 */
      query: "",
    };
  },
  /**
   * 画面初期表示に配列のソートを実施する。
   * ※初期表示のみでその後変更があってもソートしない。
   */
  created() {
    if (
      this.$attrs.modelValue &&
      this.sortOrders &&
      Array.isArray(this.$attrs.modelValue)
    ) {
      // 追加の場合には配列0件、変更でも1件の場合は、ソート不要
      if (this.$attrs.modelValue.length >= 2) {
        this.$attrs.modelValue.sort((a, b) => {
          return this.$service.sortCompare(a, b, this.sortOrders!);
        });
      }
    }
  },
  computed: {
    /**
     * vue-multiselectのpropsにデフォルト値を設定. 外部指定を優先
     * @see https://vue-multiselect.js.org/#sub-props
     */
    attrsOnDefault(): { [key: string]: unknown } {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { onInput, onBlur, onChange, ...others } = this.$attrs;
      return {
        placeholder: "",
        // 単一選択: 選択されたらドロップダウンを閉じる, 複数選択: 選択されてもドロップダウンを閉じない
        closeOnSelect: (this.$attrs.multiple as unknown) !== true,
        selectLabel: "選択",
        selectedLabel: "選択済み",
        deselectLabel: "選択解除",
        ...others,
      };
    },

    /** フィルタリング済みのoptionを返却 */
    filteredOptions(): unknown[] {
      if (this.filter && this.query) {
        return this.filter(this.query, this.options);
      } else {
        return this.options;
      }
    },
    /** エラーメッセージを表示するか */
    showErrorMessage(): boolean {
      return !!this.errorMessages;
    },
  },
  methods: {
    /** 検索条件変更 */
    searchChange(query: string): void {
      if (this.filter !== undefined) {
        this.query = query;
      }
    },
  },
});
</script>

<style lang="scss">
.multiselect {
  .multiselect__tag {
    // defaultだとタグが小さすぎ
    font-size: 1rem;
    // 色を変更
    background: #f8f9fa;
    color: black;
    border: #ced4da solid 1px;

    .multiselect__tag-icon {
      &:after {
        color: black;
      }

      &:hover {
        background: #ced4da;
      }
    }
  }

  // 選択肢をカスタマイズ + 詳細表示する際にform-groupの設定
  .multiselect__element {
    .form-group {
      margin-bottom: 8px;

      legend {
        font-weight: bold;
        font-size: 60%;
      }

      > div {
        margin-left: 0.5rem;
      }
    }
  }

  // テーブル風表示
  .multiselect-tbody {
    display: flex;

    p {
      flex-basis: 0;
      flex-grow: 1;
      margin: 0;
      padding: 0;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }
  }

  // テーブル風表示
  .multiselect-thead {
    background: #ededed;
    color: #a6a6a6;
    cursor: text;
    pointer-events: none;
    display: flex;
    padding: 12px;
    min-height: 40px;
    line-height: 16px;
    text-decoration: none;
    text-transform: none;
    vertical-align: middle;
    position: relative;
    white-space: nowrap;

    p {
      flex-basis: 0;
      flex-grow: 1;
      margin: 0;
      padding: 0;
      overflow-wrap: anywhere;
    }
  }
}
</style>
